import React, { useState, useEffect, useRef } from 'react';
import './SearchComponent.css';
import ProductCard from '../product-card/product-card';

const SearchDropdownMenu = ({
  popularSearches = [],
  popularProducts = [],
  viewBtn,
  headingPopularSearches,
  headingPopularProducts,
  headingResults,
  headingProductResults,
  noResultsText,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [activeProducts, setActiveProducts] = useState(Array.isArray(popularProducts) ? popularProducts : []);
  const [suggestedCollections, setSuggestedCollections] = useState([]);
  const searchMenuRef = useRef(null);
  const searchInputRef = useRef(null);

  const searchTerms = Array.isArray(popularSearches)
    ? popularSearches
    : (popularSearches || '').split(',').map((term) => term.trim());

  const handleOpen = () => {
    setIsOpen(true);
    const searchIconEl = document.getElementsByClassName('search_icon_header')[0];
    searchIconEl?.classList.add('active');
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
  };

  const handleClose = () => {
    setIsOpen(false);
    const searchIconEl = document.getElementsByClassName('search_icon_header')[0];
    searchIconEl?.classList.remove('active');
    setQuery('');
    setSuggestions([]);
    setActiveProducts(popularProducts);
  };

  useEffect(() => {
    window.openSearchDropdown = handleOpen;
    return () => delete window.openSearchDropdown;
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchMenuRef.current && !searchMenuRef.current.contains(e.target)) {
        handleClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = async (e) => {
    const { value } = e.target;
    setQuery(value);

    if (value.length < 2) {
      setSuggestions([]);
      setSuggestedCollections([]);
      setActiveProducts(popularProducts);
      return;
    }

    try {
      const url = `/search/suggest.json?q=${encodeURIComponent(
        value,
      )}&resources[type]=product,page,article,collection&synonyms=true`;

      const res = await fetch(url);
      const data = await res.json();
      const products = data?.resources?.results?.products || [];
      const collections = data?.resources?.results?.collections || [];

      const detailedProducts = await Promise.all(
        products.map(async (p) => {
          try {
            // const prodRes = await fetch(`/products/${p.handle}.json`);
            // const prodData = await prodRes.json();
            const prodRes = await fetch(`/products/${p.handle}?view=product-json`);
            const text = await prodRes.text();

            const prodData = JSON.parse(text);

            return prodData;
          } catch (err) {
            return p;
          }
        }),
      );

      setSuggestions(detailedProducts.slice(0, 3));
      setSuggestedCollections(collections);
      setActiveProducts(detailedProducts.slice(0, 4));
    } catch (err) {
      setSuggestions([]);
      setActiveProducts(popularProducts);
      setSuggestedCollections([]);
    }
  };
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      window.location.href = `/search?q=${encodeURIComponent(query)}`;
    }
  };

  return (
    <div
      ref={searchMenuRef}
      className={`absolute left-0 right-0 z-50 bg-white p-5 ${isOpen ? 'block' : 'hidden'}`}
    >
      {/* Search header */}
      <div className="mx-auto flex max-w-[770px] flex-col items-end items-center justify-between gap-2.5 md:flex-row md:items-center md:gap-0">
        <div className="mb-2 flex w-full items-center justify-between md:hidden">
          <span className="mobile-search-text- body-1-bold">Search</span>
          <button
            className="close-icon cursor-pointer"
            onClick={handleClose}
            aria-label="Close search"
          >
            <svg
              width={20}
              height={20}
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              aria-hidden="true"
            >
              <path
                d="M3.11648 2.88352L17.2586 17.0257"
                stroke="currentColor"
                strokeLinecap="square"
              />
              <path
                d="M17.1165 3.11648L2.97435 17.2586"
                stroke="currentColor"
                strokeLinecap="square"
              />
            </svg>
          </button>
        </div>
        <form
          className="relative w-full max-w-[720px]"
          onSubmit={(e) => e.preventDefault()}
        >
          <input
            type="text"
            className="text-sm w-full rounded-[30px] border border-black px-10 py-2 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
            placeholder="Search"
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            ref={searchInputRef}
          />
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              aria-label="Search"
              aria-hidden="true"
              className="h-5 w-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
              />
            </svg>
          </span>
        </form>
        <button
          className="text-2xl hidden cursor-pointer md:block"
          onClick={handleClose}
          aria-label="Close search"
        >
          <svg
            width={20}
            height={20}
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            aria-hidden="true"
          >
            <path
              d="M3.11648 2.88352L17.2586 17.0257"
              stroke="currentColor"
              strokeLinecap="square"
            />
            <path
              d="M17.1165 3.11648L2.97435 17.2586"
              stroke="currentColor"
              strokeLinecap="square"
            />
          </svg>
        </button>
      </div>

      {isOpen && (
        <div className="flex- mx-auto flex max-w-[700px] flex-col gap-0 p-0 md:flex-row md:gap-12 md:p-6">
          {/* Popular Searches */}
          <div className="flex-shrink-0 md:basis-[200px]">
            <p className="body-2-bold mt-4 text-grey-dark">
              {query.length >= 2 ? headingResults : headingPopularSearches}
            </p>
            {query.length < 2 && (
              <ul
                id="popular-searches-list"
                className="space-y-3"
              >
                {searchTerms.map((term, i) => (
                  <li
                    key={i}
                    className="my-3"
                  >
                    <a
                      href={`/search?q=${encodeURIComponent(term)}`}
                      className="body-1 menu-link text-default"
                    >
                      {term}
                    </a>
                  </li>
                ))}
              </ul>
            )}

            {/* Predictive Search Results */}
            <div id="predictive-results-container">
              {query.length >= 2 && (suggestions.length > 0 || suggestedCollections.length > 0) && (
                <div className="predictive-results">
                  {suggestions.slice(0, 3).map((item) => (
                    <a
                      key={item.id}
                      href={`/search?q=${encodeURIComponent(item.title)}`}
                      className="body-1 menu-link flex items-center py-2 text-default"
                    >
                      {item.title}
                    </a>
                  ))}
                  {query.length >= 2 &&
                    suggestedCollections.map((collection) => (
                      <a
                        key={`collection-${collection.id}`}
                        href={collection.url}
                        className="body-1 menu-link mt-1 flex items-center p-2 text-default"
                      >
                        <span>{collection.title}</span>
                      </a>
                    ))}
                </div>
              )}
            </div>

            {query.length >= 2 && activeProducts.length === 0 && suggestedCollections.length === 0 && (
              <p className="body-2-bold mt-4">{noResultsText}</p>
            )}
            {query.length >= 3 && (activeProducts.length > 0 || suggestedCollections.length > 0) && (
              <a
                href={`/search?q=${encodeURIComponent(query)}`}
                className="view-all-btn body-2-bold button button--primary my-[9px] cursor-pointer"
                style={{ display: query.length >= 2 ? 'inline-block' : 'none' }}
                id="view-all-results"
              >
                {viewBtn}
              </a>
            )}
          </div>

          {/* Popular Products */}
          <div className="popular-product flex-1">
            <p className="body-2-bold my-3 text-grey-dark">
              {query.length >= 2 ? headingProductResults : headingPopularProducts}
            </p>

            <div className="grid grid-cols-2 gap-4">
              {query.length >= 2 && suggestions.length > 0
                ? suggestions.slice(0, 2).map((product) => {
                    const currentVariant = product.variants?.[0] || {};

                    return (
                      <ProductCard
                        key={product.id}
                        product={product}
                        currentVariant={currentVariant}
                        placement="search-dropdown"
                        inputPrefix="search-dropdown"
                      />
                    );
                  })
                : activeProducts.map((product) => {
                    const variant = product.variants?.[0] || {};
                    return (
                      <ProductCard
                        key={product.id}
                        product={product}
                        currentVariant={variant}
                        placement="search-dropdown"
                        inputPrefix="search-dropdown"
                      />
                    );
                  })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default SearchDropdownMenu;
